import React, { memo, useState, useEffect } from 'react';
import { connect, useDispatch } from 'react-redux';
import { createStructuredSelector } from 'reselect';
import { compose } from 'redux';
import PropTypes from 'prop-types';
import { remove } from 'lodash';
import ReactPlayer from 'react-player/lazy';
import { useTranslation } from '../../../src/i18n';
import EditModeCard, { EditModeCardSection } from '../../../components/EditModeCard';
import {
  MEDIA_TYPES_IDS,
  PRIVACY_ATTRIBUTE_IDS,
  MEDIA_TAGS_TYPES_IDS,
  TP,
  MEDIA_ATTRIBUTES_TYPES_IDS,
  TAGS_AVAILABILITY_TYPES,
  VIDEO_ATTRIBUTE_SLUGS,
} from '../../../constants/consts';
import endpointConstants, { MEDIA, MEDIA_ATTRIBUTES } from '../../../constants/endpointConstants';
import {
  selectMediaAttributesOptions,
  selectLoadingFieldName,
  selectProductionVideo,
  selectVideoFormatsNames,
  selectImageFormatsNames,
} from '../selectors';
import {
  updateMediaAction,
  clientRemoveMediaAction,
  getProductionVideoAction,
  addProductionVideoAction,
  getProductionVideoFormats,
  getProductionImageFormatsAction,
} from '../actions';
import Button from '../../../components/Button';
import { updateOne, getWholeList, useBufferValueAction, createEntity, enqueueSnackbarAction } from '../../App/actions';
import EditMediaCard from '../../../components/EditComponents/EditMediaCard';
import FbCircularProgress from '../../../components/FbCircularProgress';
import { selectBufferValueData, selectPrivacyAttributes, selectPrivacyOptions } from '../../App/selectors';
import ActionStatusModal from '../../../components/ActionStatusModal';
import TextButton from '../../../components/TextButton';
import AddFieldWithButton from '../../../components/EditComponents/AddFieldWithButton';
import DropzoneInput from '../../../components/DropzoneInput';
import LoadingModal from '../../../components/LoadingModal';
import { joinMediaFormats } from '../../../utils/editCommon';
import AddMediaDetailsModal from '../../../components/EditComponents/AddMediaDetailsModal';

const VideoSection = ({
  production,
  videos,
  updatedVideo,
  privacyOptions,
  videoAttributesOptions,
  loadingMediaType,
  videoFormats,
  imagesFormats,
}) => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const [videoUrl, setVideoUrl] = React.useState('');
  const [isEditModalOpen, setEditModalOpen] = useState(false);
  const [modifyMedia, setModifyMedia] = useState(null);
  const [mediaToDelete, setMediaToDelete] = useState(null);

  const videoParams = {
    mediaType: {
      id: MEDIA_TYPES_IDS.VIDEO,
    },
    privacyAttribute: {
      id: PRIVACY_ATTRIBUTE_IDS.PUBLIC,
    },
    tags: [
      {
        tagType: { id: MEDIA_TAGS_TYPES_IDS.PRODUCTION },
        tagId: production?.id,
        availabilityTypes: [TAGS_AVAILABILITY_TYPES.GALLERY],
      },
    ],
  };

  useEffect(() => {
    dispatch(
      getWholeList({
        endpoint: endpointConstants.MEDIA,
        sagaRoutine: getProductionVideoAction,
        params: {
          queryParams: {
            media_tag_type_id: MEDIA_TAGS_TYPES_IDS.PRODUCTION,
            media_tag_id: production?.id,
            media_type_id: MEDIA_TYPES_IDS.VIDEO,
            exclude_media_attribute_slug: VIDEO_ATTRIBUTE_SLUGS.LIVESTREAM,
            exclude_media_attribute_type_id: MEDIA_ATTRIBUTES_TYPES_IDS.VIDEO,
          },
        },
      }),
    );
    if (!videoFormats?.length) {
      dispatch(
        getWholeList({
          endpoint: MEDIA_ATTRIBUTES,
          sagaRoutine: getProductionVideoFormats,
          params: {
            queryParams: {
              attribute_type_id: MEDIA_ATTRIBUTES_TYPES_IDS.VIDEO_FORMAT,
            },
          },
        }),
      );
    }
    if (!imagesFormats?.length) {
      dispatch(
        getWholeList({
          endpoint: MEDIA_ATTRIBUTES,
          sagaRoutine: getProductionImageFormatsAction,
          params: {
            queryParams: {
              attribute_type_id: MEDIA_ATTRIBUTES_TYPES_IDS.IMAGE_FORMAT,
            },
          },
        }),
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const closeEditMediaModal = () => {
    setEditModalOpen(false);
    setModifyMedia(null);
  };

  const onVideoAdd = url => {
    if (ReactPlayer.canPlay(url)) {
      const video = {
        url,
        ...videoParams,
      };
      dispatch(
        createEntity({
          endpoint: endpointConstants.MEDIA,
          sagaRoutine: addProductionVideoAction,
          fieldName: 'videoUrl',
          params: video,
          callback: () => {
            setVideoUrl('');
          },
        }),
      );
    } else {
      dispatch(
        enqueueSnackbarAction({
          message: t(`${TP}.FN_WEB_LINK_INVALID`),
          options: {
            variant: 'error',
          },
        }),
      );
    }
  };

  const updateMedia = (updatedMedia, videoPreview) => {
    dispatch(
      updateOne({
        endpoint: MEDIA,
        id: updatedMedia.id,
        sagaRoutine: useBufferValueAction,
        callback: entity => dispatch(updateMediaAction(entity, 'video')),
        params: { data: updatedMedia },
        ...(videoPreview?.filename ? { files: { [videoPreview.filename]: videoPreview.file } } : {}),
      }),
    );
    closeEditMediaModal();
  };

  const updatePrivacy = (value, updatedMedia) => {
    dispatch(
      updateOne({
        endpoint: MEDIA,
        id: updatedMedia.id,
        sagaRoutine: useBufferValueAction,
        callback: entity => dispatch(updateMediaAction(entity, 'video')),
        params: { privacyAttribute: { id: value } },
      }),
    );
  };

  const updateMediaDetails = (data, id, videoPreview) => {
    if (id) {
      dispatch(
        updateOne({
          endpoint: MEDIA,
          id,
          sagaRoutine: useBufferValueAction,
          params: data,
          callback: entity => {
            closeEditMediaModal();
            dispatch(updateMediaAction(entity, 'video'));
          },
          ...(videoPreview?.filename ? { files: { [videoPreview.filename]: videoPreview.file } } : {}),
        }),
      );
    } else {
      closeEditMediaModal();
    }
  };

  const deleteMedia = () => {
    const mediaTags = [...(mediaToDelete?.tags || [])];
    remove(mediaTags, tag => tag?.tagType?.id === MEDIA_TAGS_TYPES_IDS.PRODUCTION && tag?.tagId === production?.id);
    dispatch(
      updateOne({
        endpoint: endpointConstants.MEDIA,
        sagaRoutine: useBufferValueAction,
        id: mediaToDelete?.id,
        params: {
          tags: mediaTags,
        },
        callback: deletedItem => {
          dispatch(clientRemoveMediaAction(deletedItem?.id, 'video'));
          closeConfirmationModal();
        },
      }),
    );
  };

  const closeConfirmationModal = () => {
    setMediaToDelete(null);
  };

  const uploadVideo = files => {
    const video = files[0];
    const imageData = {
      file: { data: `@${video?.filename}` },
      ...videoParams,
    };
    dispatch(
      createEntity({
        endpoint: MEDIA,
        sagaRoutine: addProductionVideoAction,
        fieldName: 'videoFile',
        ...{ params: imageData, files: { [video?.filename]: video?.file } },
      }),
    );
  };

  return (
    <>
      <EditModeCard>
        <EditModeCardSection first>
          <div>{t(`${TP}.FN_ADD_VIDEO`)}</div>
          <div>
            {videos?.loading ? (
              <FbCircularProgress />
            ) : (
              videos?.data?.map(video => (
                <EditMediaCard
                  media={video}
                  mediaType="video"
                  key={`vid-${video.id}`}
                  editClick={() => {
                    setModifyMedia(video);
                    setEditModalOpen(true);
                  }}
                  privacyOptions={privacyOptions}
                  onPrivacyChange={e => updatePrivacy(e.value, video)}
                  onDelete={() => setMediaToDelete(video)}
                />
              ))
            )}
          </div>
          <DropzoneInput
            type="video"
            title={<div>{t(`${TP}.FN_UPLOAD_VIDEO_TITLE`)}</div>}
            subtitle={t(`${TP}.FN_UPLOAD_FILE_FORMAT_TEMPLATE`, {
              format: joinMediaFormats(videoFormats),
            })}
            onFileUpload={uploadVideo}
            accept={videoFormats}
          />
          <AddFieldWithButton
            value={videoUrl}
            onChange={setVideoUrl}
            onAdd={onVideoAdd}
            isLoading={loadingMediaType === 'videoUrl'}
            placeholder={`${TP}.FN_ADD_VIDEO_LINK`}
            label={`${TP}.FN_ADD_VIDEO_LINK_TITLE`}
          />
        </EditModeCardSection>
      </EditModeCard>
      {isEditModalOpen && (
        <AddMediaDetailsModal
          isOpened={isEditModalOpen}
          submitFunc={(data, mediaId, videoPreview) => {
            if (modifyMedia.preupId) {
              updateMedia(
                { ...modifyMedia, title: data.tile, tags: [...modifyMedia.tags, ...data.tags] },
                videoPreview,
              );
            } else {
              updateMediaDetails(data, mediaId, videoPreview);
            }
          }}
          onClose={() => setEditModalOpen(false)}
          entity={modifyMedia}
          type={modifyMedia?.mediaType?.slug}
          typeOptions={videoAttributesOptions}
          isLoading={updatedVideo?.loading}
          fixedProduction
          imageFormats={imagesFormats}
        />
      )}
      {mediaToDelete && (
        <ActionStatusModal
          isOpened
          onClose={closeConfirmationModal}
          type="warning"
          title={t(`${TP}.FN_DELETE_MEDIA_CONFIRMATION`)}
          buttons={[
            <TextButton onClick={deleteMedia}>{t(`${TP}.m_DELETE`)}</TextButton>,
            <Button onClick={closeConfirmationModal}>{t(`${TP}.m_CANCEL`)}</Button>,
          ]}
        >
          {updatedVideo?.loading && <FbCircularProgress />}
        </ActionStatusModal>
      )}
      {loadingMediaType === 'videoFile' && <LoadingModal title={t(`${TP}.FN_UPLOADING_VIDEO`)} />}
    </>
  );
};

VideoSection.propTypes = {
  production: PropTypes.object,
  updatedVideo: PropTypes.object,
  videoFormats: PropTypes.array,
};

const mapStateToProps = createStructuredSelector({
  videos: selectProductionVideo(),
  updatedVideo: selectBufferValueData(),
  privacyOptions: selectPrivacyOptions(),
  privacyAttributes: selectPrivacyAttributes(),
  videoAttributesOptions: selectMediaAttributesOptions(MEDIA_ATTRIBUTES_TYPES_IDS.VIDEO),
  loadingMediaType: selectLoadingFieldName(),
  videoFormats: selectVideoFormatsNames(),
  imagesFormats: selectImageFormatsNames(),
});

const withConnect = connect(mapStateToProps, null);

export default compose(memo, withConnect)(VideoSection);
